/**
 * Feb 2026 R293zze · LinkedPreviewDialog
 *
 * Lightweight read-only preview dialog for an RFQ or standalone
 * Estimate hit from a `LinkedBadges` popover. Keeps the review loop
 * tight — no tab-switch — while still letting the user jump into the
 * full page when they need to edit.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_TONE = {
  draft:            "bg-slate-100 text-slate-800 border-slate-300",
  submitted:        "bg-sky-100 text-sky-800 border-sky-300",
  in_estimation:    "bg-amber-100 text-amber-800 border-amber-300",
  quoted:           "bg-emerald-100 text-emerald-800 border-emerald-300",
  approved:         "bg-emerald-100 text-emerald-800 border-emerald-300",
  pending_internal: "bg-sky-100 text-sky-800 border-sky-300",
  pending_client:   "bg-indigo-100 text-indigo-800 border-indigo-300",
  revising:         "bg-amber-100 text-amber-800 border-amber-300",
  rejected:         "bg-rose-100 text-rose-800 border-rose-300",
  cancelled:        "bg-rose-100 text-rose-800 border-rose-300",
};

export default function LinkedPreviewDialog({ open, onClose, ws, kind, docId }) {
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !ws || !docId || !kind) return;
    setLoading(true); setError(""); setDoc(null);
    (async () => {
      try {
        const url = kind === "rfq"
          ? `/rfqs/${docId}`
          : `/estimates/${docId}`;
        const r = await api.get(url);
        setDoc(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Not found");
      } finally { setLoading(false); }
    })();
  }, [open, ws, kind, docId]);

  const fullPagePath = kind === "rfq"
    ? `/app/rfqs/${docId}`
    : `/app/estimates/${docId}`;

  const label = kind === "rfq" ? "RFQ" : "Estimate";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="linked-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {label}
            <span className="font-mono text-sm text-primary">
              {doc?.number || doc?.doc_no || "…"}
            </span>
          </DialogTitle>
          {doc?.title && <DialogDescription>{doc.title}</DialogDescription>}
        </DialogHeader>
        <div className="min-h-[200px] space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {error && !loading && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 p-3">
              {error}
            </div>
          )}
          {doc && !loading && (
            <>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <Field label="Status">
                  {doc.status && (
                    <span className={`inline-flex items-center px-2 py-0.5 border text-[11px] font-semibold uppercase tracking-wide
                                       ${STATUS_TONE[doc.status] || "bg-slate-100 text-slate-800 border-slate-300"}`}>
                      {doc.status.replace(/_/g, " ")}
                    </span>
                  )}
                </Field>
                <Field label="Client">{doc.client_name || doc.customer_name || "—"}</Field>
                <Field label="Brand">{doc.brand_name || "—"}</Field>
                {kind === "estimate" && (
                  <>
                    <Field label="Grand total">
                      {typeof doc.grand_total === "number"
                        ? `₹${doc.grand_total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                        : "—"}
                    </Field>
                    <Field label="Line items">{(doc.lines || doc.items || []).length}</Field>
                    <Field label="Valid until">{doc.valid_until || "—"}</Field>
                  </>
                )}
                {kind === "rfq" && (
                  <>
                    <Field label="Priority">{doc.priority || "—"}</Field>
                    <Field label="Deadline">{doc.deadline || "—"}</Field>
                    <Field label="Owner">{doc.owner_name || "—"}</Field>
                  </>
                )}
              </div>
              {doc.description && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                    Description
                  </div>
                  <div className="text-sm whitespace-pre-wrap border border-border bg-muted/30 p-2 max-h-48 overflow-y-auto">
                    {doc.description}
                  </div>
                </div>
              )}
              {(doc.lines || doc.items || []).length > 0 && kind === "estimate" && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                    Line items
                  </div>
                  <div className="border border-border max-h-60 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted uppercase tracking-wide text-[10px]">
                        <tr>
                          <th className="text-left p-1.5">Description</th>
                          <th className="text-right p-1.5">Qty</th>
                          <th className="text-right p-1.5">Rate</th>
                          <th className="text-right p-1.5">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(doc.lines || doc.items || []).slice(0, 20).map((ln, i) => (
                          <tr key={ln.id || i} className="border-t border-border">
                            <td className="p-1.5">{ln.description || ln.name || "—"}</td>
                            <td className="p-1.5 text-right tabular-nums">{ln.quantity ?? ln.qty ?? "—"}</td>
                            <td className="p-1.5 text-right tabular-nums">
                              {typeof ln.rate === "number" ? ln.rate.toLocaleString("en-IN") : "—"}
                            </td>
                            <td className="p-1.5 text-right tabular-nums">
                              {typeof ln.amount === "number" ? ln.amount.toLocaleString("en-IN") : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex justify-between items-center pt-3 border-t border-border">
          <Button variant="ghost" onClick={onClose} size="sm" className="rounded-none"
                     data-testid="linked-preview-close">
            Close
          </Button>
          <Link to={fullPagePath} onClick={onClose}>
            <Button size="sm" className="rounded-none"
                       data-testid="linked-preview-open-full">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open full page
            </Button>
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
        {label}
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}
